import type { DefaultObject, EventCallback, InnerEvents } from '../types';

import Events from './events';

const consoleIgnore: string[] = [];

class Dispatcher {
    events: InnerEvents;
    eventId: number;
    debug: boolean;

    constructor(debug = false) {
        this.events = {};
        this.eventId = 0;

        this.debug = debug;
    }

    on(
        eventName: number,
        callback: EventCallback,
        context: DefaultObject = {},
        extraData: DefaultObject = {},
        once = false
    ) {
        if (!eventName) {
            throw new Error('Dispatcher: no event name passed');
        }

        if (typeof eventName !== 'number') {
            throw new TypeError('Dispatcher: event name should be a number');
        }

        if (!callback) {
            throw new Error('Dispatcher: no callback passed');
        }

        if (typeof callback !== 'function') {
            throw new Error('Dispatcher: callback not a function');
        }

        if (!Object.keys(this.events).includes(`${eventName}`)) {
            this.events[eventName] = {};
        }

        this.eventId++;
        this.events[eventName][this.eventId] = { callback, context, once, extraData };

        return this.eventId;
    }

    once(
        eventName: number,
        callback: EventCallback,
        context: DefaultObject = {},
        extraData: DefaultObject = {}
    ) {
        return this.on(eventName, callback, context, extraData, true);
    }

    offAllEvents() {
        this.events = {};
    }

    offMany(eventsData: [eventName: number, eventId: number][] = []) {
        eventsData.forEach(([eventName, eventId]) => {
            const event = this.events?.[eventName]?.[eventId];

            if (event) {
                if (this.debug) {
                    console.info('Event listener unbound: ', eventName, eventId, event);
                }

                delete this.events[eventName][eventId];
            }
        });
    }

    off(eventName: number, eventId: number) {
        if (!eventName || !eventId) {
            console.warn('Dispatcher error: incorrect eventName or eventId passed');
            return;
        }

        const eventData = this.events[eventName];

        if (eventData && eventData[eventId]) {
            delete eventData[eventId];
        }
    }

    logEmittedEvent(eventName: number, eventData: unknown[]) {
        if (this.debug) {
            const event = Object.keys(Events)[Object.values(Events).indexOf(eventName)];

            if (consoleIgnore.includes(event)) {
                return;
            }

            try {
                throw new Error();
            } catch (e: unknown) {
                if (e instanceof Error) {
                    const stack = e.stack;

                    console.info(`Event emitted: ${event}`, eventData, stack);
                }
            }
        }
    }

    deleteEvent(eventName: number, eventId: number) {
        try {
            delete this.events[eventName][eventId];
        } catch (e) {
            console.info(e);
        }
    }

    emit(eventName: number, ...eventData: unknown[]) {
        this.logEmittedEvent(eventName, eventData);

        const events = this.events[eventName];

        if (!events || !Object.keys(events).length) {
            return;
        }

        Object.keys(events).forEach((stringId: string) => {
            const eventId = Number(stringId);

            if (Number.isNaN(eventId)) {
                return;
            }

            const event = events[eventId];

            if (!event.context) {
                this.deleteEvent(eventName, eventId);

                return;
            }

            if (event.extraData) {
                eventData.push(event.extraData);
            }

            if (event.once) {
                this.deleteEvent(eventName, eventId);
            }

            event.callback.apply(event.context, eventData);
        });
    }
}

const dispatcher = new Dispatcher();
export default dispatcher;
